import { NumbersSharp } from '@mui/icons-material';
import { SwipeableDrawer, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { Calendar } from '@phosphor-icons/react';
import moment from 'jalali-moment';
import { useEffect, useState } from 'react';
import { buildStyles, CircularProgressbar, CircularProgressbarWithChildren } from 'react-circular-progressbar';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { Loading } from '../../components';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { urls } from '../../services/endPoint';
import { api } from '../../services/http';
import { useAppSelector } from '../../services/redux/store';
import { enNumToPersian, formatPrice, persianNumToEn } from '../../utils/utils';
import 'react-circular-progressbar/dist/styles.css';
import DatePicker from 'react-multi-date-picker';
import Icon from 'react-multi-date-picker/components/icon';

const defaultRange = [
  moment().subtract(1, 'month').startOf('jmonth').startOf('day').locale('fa').format('jYYYY/jMM/jDD'),
  moment().startOf('jmonth').locale('fa').format('jYYYY/jMM/jDD'),
]

const translations = {
  firstPeriodTotal: "کل درآمد بازه اول ماه",
  firstPeriodProfit: "خالص دریافتی بازه اول ماه",
  firstPeriodCount: "تعداد سفارشات بازه اول ماه",
  secondPeriodTotal: "کل درآمد بازه دوم ماه",
  secondPeriodProfit: "خالص دریافتی بازه دوم ماه",
  secondPeriodCount: "تعداد سفارشات بازه دوم ماه",

} as const;

const Bill = () => {
  const [transactions, setTransactions] = useState([]);
  const [transDetailModal, setTransDetailModal] = useState({ open: false, data: undefined });
  const [chartData, setChartData] = useState([]);
  const [isLoadings, setIsLoadings] = useState({
    periodData: true,
    incomeData: false,
    chartData: true
  });
  const [incomeData, setIncomeData] = useState<any>({
    total: 1,
    profit: 0,
    lastTransactionDate: '-'
  });
  const [periodRange, setPeriodRange] = useState([]);
  const [periodChanged, setPeriodChanged] = useState(false);
  const [periodData, setPeriodData] = useState<any>({
    total: 1,
    profit: 0,
  });



  const transactionList = () => {
    return transactions?.map((transaction, index) =>
      <div className='transactionsItem' key={transaction.id}>
        <div className='transactionsItemCol'>
          <span className='transactionsItemColRow'>
            <Calendar size={20}/>
            <span>{transaction.date}</span>
          </span>
          <span className='transactionsItemColRow'>
            <span>کد رهگیری:</span>
            <span>{transaction.code}</span>
          </span>
        </div>
        <div className='transactionsItemCol left'>
          <span className='transactionsItemColRow'>{formatPrice(transaction.amount)} تومان </span>
          <span className='transactionsDetailButton' onClick={() => setTransDetailModal({ open: true, data: transaction })}>
            جزئیات
          </span>
        </div>
      </div>
    )
  };

  function addLabels<T extends { dataKey: keyof typeof translations }>(series: T[]) {
    return series.map((item) => ({
      ...item,
      label: translations[item.dataKey],
      valueFormatter: (v: number | null) => (v ? `${v.toLocaleString()}` : '-'),
    }));
  }

  const fetchData = async () => {
    const res = await Promise.all([
      api(urls.transaction, {}, true),
      api(urls.billChart, {}, true),
      ]);

    if (res[0].code == 200) {
      setTransactions(res[0].data);
    }
    if (res[1].code == 200) {
      setChartData(res[1].data);
    }

  };

  const fetchIncomeData = async () => {
    setIsLoadings(prev => ({
      ...prev,
      incomeData: true
    }));
    const res = await api(`${urls.workerIncome}`, {}, true);
    if (res.code == 200) {
      setIncomeData({
        total: res.data.total,
        profit: res.data.profit,
        lastTransactionDate: res.data.lastTransactionDate
      });
    }
    setIsLoadings(prev => ({ ...prev, incomeData: false }))
  }

  const fetchPeriodData = async (from = defaultRange[0],
                                  to = defaultRange[1]) => {
    const res = await api(`${urls.workerDashboard}?from=${moment(from, 'jYYYY/jMM/jDD').format('jYYYY-jMM-jDD-00-00')}&to=${moment(to, 'jYYYY/jMM/jDD').format('jYYYY-jMM-jDD-23-59')}` , { }, true)
    setPeriodData({
      total: res.data.total,
      profit: res.data.profit,
    })
    setPeriodChanged(false);
    setIsLoadings(prev => ({
      ...prev,
      periodData: false
    }))
  };

  useEffect(() => {
    fetchData()
    fetchPeriodData();
    fetchIncomeData()
  }, []);

  return (
    <main className="workerDashboardMain">
      <h3 className='billTitle'>آمار خالص دریافتی/درآمد کل</h3>
      <section className="billSection">
        <article className="billCard">
          <div className="billCircleHeader">
            <h4>تسویه نشده</h4>
            <i className="refreshIcon" onClick={fetchIncomeData}></i>
          </div>
          <CircularProgressbarWithChildren
            value={incomeData.profit / incomeData.total * 100}
            styles={buildStyles({
              textSize: '10px',
              pathTransitionDuration: 3,
              pathColor: '#c89dc7',
              textColor: '#f88',
            })}
          >
            {isLoadings.incomeData ? <LoadingSpinner /> :
              <div className="billCircleContent">
              <span>{formatPrice(incomeData.profit) || '-'}</span>
              <hr className="billCircleContentLine"/>
              <span>{incomeData.total > 1 ? formatPrice(incomeData.total) : '-'}</span>
            </div>
            }
          </CircularProgressbarWithChildren>
          <span>تاریخ آخرین تسویه</span>
          <span>{enNumToPersian(incomeData.lastTransactionDate)}</span>
        </article>
        <article className="billCard">
          <div className="billCircleHeader">
            <h4>بازه زمانی انتخابی</h4>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              value={periodRange}
              onChange={(date, context) => {
                setPeriodChanged(true);
                setPeriodRange([context.validatedValue[0], context.validatedValue[1]]);
              }}
              render={<Icon color={'#c89dc7'}/>}
              onClose={() => {
                if (periodChanged) {
                  setIsLoadings(prev => ({
                    ...prev,
                    periodData: true
                  }));
                  fetchPeriodData(persianNumToEn(periodRange[0]), persianNumToEn(periodRange[1]));
                }
              }}
              range/>
          </div>
          <CircularProgressbarWithChildren
            value={periodData.profit/periodData.total * 100}
            styles={buildStyles({
              textSize: '10px',
              pathTransitionDuration: 3,
              pathColor: '#c89dc7',
              textColor: '#f88',
            })}
          >
            {isLoadings.periodData ? <LoadingSpinner /> :
              <div className="billCircleContent">
                <span>{formatPrice(periodData.profit) || '-'}</span>
                <hr className="billCircleContentLine"/>
                <span >{periodData.total > 1 ? formatPrice(periodData.total) : '-'}</span>
              </div>
            }
          </CircularProgressbarWithChildren>
          <span>از {periodRange[0] || enNumToPersian(defaultRange[0])}</span>
          <span>تا {periodRange[1] || enNumToPersian(defaultRange[1])}</span>
        </article>
      </section>
      <h3 className='billTitle'>نمودار کارکرد ماهیانه</h3>
      <section className="billCard">
        <div className="chartContainer">
          {chartData.length > 0 &&
              <>
            <BarChart
              dataset={chartData}
              series={addLabels([
                { dataKey: 'firstPeriodProfit', stack: 'firstPeriod' },
                { dataKey: 'firstPeriodTotal', stack: 'firstPeriod' },
                { dataKey: 'secondPeriodProfit', stack: 'secondPeriod' },
                { dataKey: 'secondPeriodTotal', stack: 'secondPeriod' },
              ])}
              xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
              slotProps={{ legend: { position: { horizontal: 'right', vertical: 'top' },labelStyle: { top: '150px', fontSize: '8px' } }}}
              sx={{ width: '100%'}}
              height={350}
              margin={{ left: 80 }}
              tooltip={{ trigger: 'axis'}}
              colors={['#843b62', '#f67e7d', '#ffb997', '#da627d']}
              barLabel={(item, context) => {
                return Number(item.seriesId.toString().at(item.seriesId.toString().length - 1)) % 2 != 0 && (Object.entries(chartData[item.dataIndex]).find(([key, value]) => value == item.value)[0].includes('second') ? chartData[item.dataIndex].secondPeriodCount : chartData[item.dataIndex].firstPeriodCount)
              }}
            />
                  <Typography fontSize={'12px'}>(اعداد نشان دهنده تعداد سفارش در هر بازه می باشند)</Typography>
              </>
          }
        </div>
      </section>
      {/* <section className="banners"> */}
        {/* <div className="dashCard"> */}
        {/*   <span>کل درآمد {moment().subtract(1, 'month').locale('fa').format('jMMMM')}</span> */}
          {/* <span>{formatPrice(salaries.month)} تومان</span> */}
        {/* </div> */}
      {/* </section> */}
      <h3 className='billTitle'>تراکنش ها</h3>
      <section className='transactions'>
        {transactionList()}
      </section>
      <SwipeableDrawer
        anchor="bottom"
        open={transDetailModal.open}
        onClose={() => setTransDetailModal({ open: false, data: undefined })}
        onOpen={() => {}}
      >
        <div className='transactionModal'>
          {transDetailModal.data?.description &&
            <div>
              <h3>جزئیات</h3>
                <span>{transDetailModal.data?.description}</span>
            </div>
          }
          <h3>رسید پرداخت</h3>
          <img src={transDetailModal.data?.media?.url} alt={transDetailModal.data?.code}/>
        </div>  
      </SwipeableDrawer>
    </main>
  );
}
export default Bill;
